import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, ProxAgFns, Roles, RowSelectionMultipleCheckbox, UtilDialogService, wrapBusy } from '@core';
import {
  PricedProductType,
  ProgramProductCategoryTag,
  ProgramProductCategoryTagMap,
  ProgramProductTypeTag,
  ProgramProductTypeTagMap,
} from '@models';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { EntityFns } from '@data';
import { ProgramProductTypeTagFinderDialog } from './program-product-type-tag-finder.dialog';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { UtilFns } from '@utils';
import { ProgramTestMenuDialogComponent } from './program-test-menu-dialog.component';

@Component({
  selector: 'prox-program-product-type-tags-sub',
  templateUrl: './program-product-type-tags.sub.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-program-product-type-tags-sub {
        .p-tabpanels {
          padding: 0 !important;
        }
      }

      prox-program-product-type-tags-sub {
        .p-splitter {
          background: none !important;
          border: none !important;
        }
      }

      prox-program-product-type-tags-sub {
        prox-field label {
          margin-left: 5px;
          font-weight: 600;
          font-size: 1rem;
        }
      }
    `,
  ],
})
export class ProgramProductTypeTagsSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  savedRefreshId?: number;

  ppttGridOptions!: GridOptionsExt;
  pptts?: ProgramProductTypeTag[];
  selectedPptt?: ProgramProductTypeTag;

  ppctGridOptions!: GridOptionsExt;
  ppcts?: ProgramProductCategoryTag[];
  selectedPpct?: ProgramProductCategoryTag;

  tmapGridOptions!: GridOptionsExt;
  tmaps: ProgramProductTypeTagMap[] = [];
  availablePricedProductTypes!: Set<PricedProductType>;
  buttonProps = { calcDisabled: () => this.parent.isReadOnly() };

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  async ngOnInit() {
    this.ppttGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPpttGridReady,
      onRowSelected: this.onPpttRowSelected,
    });

    this.ppctGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onPpctGridReady,
      },
      {
        detailProperty: 'programProductCategoryTagMaps',
      },
    );
    this.updatePpctMasterDetail(this.ppctGridOptions);

    this.tmapGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onTmapGridReady,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as ProgramProductTypeTagMap;
        return x.pricedProductTypeId;
      },
    });

    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh(true);
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    this.availablePricedProductTypes = new Set(this.program.programProductTypeConfigs.map((x) => x.productTypeConfig.pricedProductType));

    this.pptts = this.parent.pptts.filter((x) => {
      const hasPpt = x.programProductTypeTagMaps.some((y) => this.availablePricedProductTypes.has(y.pricedProductType));
      return hasPpt;
    });

    const isLoaded = this.program.entityAspect.isNavigationPropertyLoaded('programProductCategoryTags');
    if (isLoaded && !forceRefresh) {
      this.ppcts = this.program.programProductCategoryTags;
    } else {
      this.ppcts = await this.dbQueryService.getProgramProductCategoryTags(this.program.id);
      this.program.entityAspect.markNavigationPropertyAsLoaded('programProductCategoryTags');
    }

    // needed to refresh editability
    this.onPpttGridReady(this.ppttGridOptions);
    AgFns.refreshGrid(this.ppttGridOptions, this.pptts);

    this.onPpctGridReady(this.ppctGridOptions);
    this.updatePpctMasterDetail(this.ppctGridOptions);
    await AgFns.refreshGrid(this.ppctGridOptions, this.ppcts);

    AgFns.sizeColumnsToFit(this.ppttGridOptions);
    AgFns.sizeColumnsToFit(this.ppctGridOptions);

    // This is needed if the forceRefresh is as a result of an Undo operation.
    if (forceRefresh) {
      this.ppttGridOptions.apiX?.deselectAll();
      this.ppctGridOptions.apiX?.deselectAll();
    }
    AgFns.selectDefaultRow(this.ppttGridOptions);
  }

  onPpttGridReady(event: GridReadyEvent | GridOptionsExt) {
    const [colDefs, sortModel] = this.getPpttColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.refreshGrid(this.ppttGridOptions, this.pptts);
  }

  getPpttColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        headerName: 'Name',
        field: 'name',
        editable: false,
        ...AgFns.flexWidth(200),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  onPpttRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const pptt = event.data as ProgramProductTypeTag;
    this.selectedPptt = pptt;
    if (pptt == null) return;

    if (pptt.id !== UtilFns.EmptyGuid && pptt.entityAspect.entityState.isDeleted()) {
      this.selectedPptt = undefined;
      this.ppttGridOptions.apiX?.deselectAll();
      return;
    }

    this.tmaps = pptt.programProductTypeTagMaps.filter((x) => this.availablePricedProductTypes.has(x.pricedProductType));

    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
  }

  // ----- Apct grid -------------------------------

  onPpctGridReady(event: GridReadyEvent | GridOptionsExt) {
    const [colDefs, sortModel] = this.getPpctColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPpctColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'name',
        editable: () => !this.parent.isReadOnly(),
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(200),
      },
      {
        ...AgFns.createButtonProps('', this.onTagAttach.bind(this), {
          label: 'Attach Tag',
          ...this.buttonProps,
          canDisplay: () => !this.parent.isReadOnly(),
        }),
        ...AgFns.fixWidth(100),
      },
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onPpctDelete.bind(this), this.buttonProps)),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  updatePpctMasterDetail(parentGridOptions: GridOptionsExt) {
    parentGridOptions.masterDetail = true;
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'Tag Name', field: 'programProductTypeTag.name', filter: 'agTextColumnFilter', flex: 1 },
      {
        ...AgFns.createButtonProps('', this.onTagDetach.bind(this), {
          label: 'Detach Tag',
          ...this.buttonProps,
          canDisplay: () => !this.parent.isReadOnly(),
        }),
        ...AgFns.fixWidth(100),
      },
    ];
    detailGridOptions.getRowId = (rowIdParams: GetRowIdParams) => {
      const x = rowIdParams.data as ProgramProductCategoryTagMap;
      return x.programProductTypeTagId;
    };
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      refreshStrategy: 'everything',
      getDetailRowData: (params) => {
        const apct = params.data as ProgramProductCategoryTag;
        params.successCallback(apct.programProductCategoryTagMaps);
      },
    };
  }

  async onPpctAdd() {
    const apct = this.dbSaveService.createEntity(ProgramProductCategoryTag, { programId: this.program.id });
    apct.entityAspect.validateEntity();
    this.ppcts?.push(apct);
    AgFns.refreshGrid(this.ppctGridOptions, this.ppcts);
  }

  async onPpctDelete(ppct: ProgramProductCategoryTag) {
    // Check if form has unsaved changes
    if (this.parent.hasChanges()) {
      this.toastr.warning('Please save or cancel your changes before deleting a menu category.');
      return;
    }

    // Confirm delete
    const ok = await this.dialogService.askIfOk('Delete Menu Category', `Are you sure you want to delete menu category "${ppct.name}"?`);
    if (!ok) return;

    // Delete related entities
    ppct.programProductCategoryTagMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(ppct.entityAspect);

    // Atomic save
    try {
      await this.dbSaveService.saveChanges();
      this.toastr.success('Menu category deleted successfully.');

      // Update UI
      if (this.ppcts) {
        _.remove(this.ppcts, ppct);
        AgFns.refreshGrid(this.ppctGridOptions, this.ppcts);
        AgFns.refreshGrid(this.ppctGridOptions.detailCellRendererParams?.detailGridOptions);
      }
    } catch (error) {
      this.toastr.error('Failed to delete menu category.');
      throw error;
    }
  }

  onTestMenu() {
    ProgramTestMenuDialogComponent.openDialog(this.pngDialogService, {
      program: this.program,
    });
  }

  @wrapBusy
  async onTagAttach(row: ProgramProductCategoryTag) {
    if (this.pptts == null) return;
    const attachedPpttIds = new Set(row.programProductCategoryTagMaps.map((x) => x.programProductTypeTagId));
    const missingPptts = this.pptts.filter((x) => !attachedPpttIds.has(x.id));
    const attachPptts = await this.dialogService.createFinder(ProgramProductTypeTagFinderDialog, {
      pptts: missingPptts,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    attachPptts.forEach((aptt) => {
      if (!this.dbSaveService.uow.undoIfDeleted(ProgramProductCategoryTagMap, [row.id, aptt.id])) {
        this.dbSaveService.createEntity(ProgramProductCategoryTagMap, {
          programProductCategoryTagId: row.id,
          programProductTypeTagId: aptt.id,
        });
      }
    });
    // HACK - AgGrid issue - complete hack - needed to show expando on grid  if row doesn't already have it.
    await AgFns.refreshGrid(this.ppctGridOptions, []);
    await AgFns.refreshGrid(this.ppctGridOptions, this.ppcts);
    const rowNode = this.ppctGridOptions.apiX?.getRowNode(row.id);
    rowNode?.setExpanded(true);
    await AgFns.refreshDetailRowHeights(this.ppctGridOptions);
  }

  @wrapBusy
  async onTagDetach(row: ProgramProductCategoryTagMap) {
    if (this.ppcts == null) return;
    const rowNode = this.ppctGridOptions.apiX?.getRowNode(row.programProductCategoryTag.id);
    EntityFns.deleteOrDetach(row.entityAspect);

    AgFns.refreshGrid(this.ppctGridOptions, this.ppcts);
    rowNode?.setExpanded(true);

    AgFns.refreshDetailRowHeights(this.ppctGridOptions);
  }

  // ------------- Tmap grid --------------------------

  onTmapGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getTmapColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getTmapColDefsAndSortModel() {
    const ptypePath = 'pricedProductType.productType';
    const colDefs: ColDef[] = [
      { ...ProxAgFns.getSupplierColDef(ptypePath + '.supplier.name') },
      { ...ProxAgFns.getManufacturerColDef(ptypePath + '.manufacturer.name') },
      { ...ProxAgFns.getProductTypeNameColDef(ptypePath + '.name') },
      { ...ProxAgFns.getProductTypeDescriptionColDef(ptypePath + '.description') },

      { headerName: 'Base Price', field: 'pricedProductType.basePrice', ...AgFns.fixWidth('money') },
    ];
    const sortModel: ISortModel = [
      { colId: ptypePath + '.supplier.name', sort: 'asc' },
      { colId: ptypePath + '.manufacturer.name', sort: 'asc' },
      { colId: ptypePath + 'name', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }
}
